import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, db } from './firebaseConfig';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { Info } from 'lucide-react';
import './LinkedInTheme.css';

function ActivateAgent() {
  const { t } = useTranslation();
  const { userProfile, createdJds } = useOutletContext() || {}; // Get profile and JDs
  const [user] = useState(auth.currentUser);
  const [isActivated, setIsActivated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [vtcId, setVtcId] = useState(null);
  const [simulations, setSimulations] = useState([]); // For HM/Admin view

  // Fallback avatar logic
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";

  const isHmOrAdmin = userProfile?.roles?.includes('hm') || userProfile?.roles?.includes('admin');

  useEffect(() => {
    const fetchProfileAndSimulations = async () => {
      if (user) {
        // 1. Profile & Activation Status
        if (userProfile && userProfile.simulationsEnabled !== undefined) {
             setIsActivated(userProfile.simulationsEnabled);
        } else {
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
               setIsActivated(userDoc.data().profile?.simulationsEnabled || false);
            }
        }
        
        // 2. Fetch VTC ID
        try {
            const q = query(collection(db, 'talentCards'), where('userId', '==', user.uid), limit(1));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                setVtcId(querySnapshot.docs[0].id);
            }
        } catch (e) {
            console.error("Error fetching VTC ID", e);
        }

        // 3. Fetch Simulations (if HM/Admin)
        if (isHmOrAdmin && createdJds && createdJds.length > 0) {
            try {
                // Chunk JDs into batches of 10 due to Firestore 'in' query limit
                const jdIds = createdJds.map(jd => jd.id);
                const chunks = [];
                for (let i = 0; i < jdIds.length; i += 10) {
                    chunks.push(jdIds.slice(i, i + 10));
                }

                const promises = chunks.map(chunk => {
                    const simQ = query(collection(db, 'simulations'), where('jd_id', 'in', chunk));
                    return getDocs(simQ);
                });

                const snapshots = await Promise.all(promises);
                const allSims = snapshots.flatMap(snap => snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                
                // Sort by date desc
                allSims.sort((a, b) => {
                    const dateA = a.created_at?.toDate ? a.created_at.toDate() : new Date(0);
                    const dateB = b.created_at?.toDate ? b.created_at.toDate() : new Date(0);
                    return dateB - dateA;
                });
                setSimulations(allSims);
            } catch (e) {
                console.error("Error fetching simulations", e);
            }
        }
        
        setIsLoading(false);
      } else {
          setIsLoading(false);
      }
    };
    fetchProfileAndSimulations();
  }, [user, userProfile, isHmOrAdmin, createdJds]);

  const handleActivation = async () => {
    if (!user) return;
    setIsLoading(true);
    const userDocRef = doc(db, 'users', user.uid);
    try {
      await updateDoc(userDocRef, {
        'profile.simulationsEnabled': true,
        'profile.simulationsActivatedAt': serverTimestamp(),
        'status': 'active'
      });
      setIsActivated(true);
    } catch (error) {
      console.error("Error activating simulations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivation = async () => {
     if (!user) return;
     if (!window.confirm('Are you sure you want to deactivate your agent? It will stop participating in AI-to-AI screening interviews.')) return;

     setIsLoading(true);
     const userDocRef = doc(db, 'users', user.uid);
     try {
       await updateDoc(userDocRef, {
         'profile.simulationsEnabled': false,
         'status': 'inactive'
       });
       setIsActivated(false);
     } catch (error) {
       console.error("Error deactivating simulations:", error);
     } finally {
       setIsLoading(false);
     }
  };

  // Helper to render status style
  const getStatusStyle = (status) => {
    const lowerStatus = status.toLowerCase();
    switch (lowerStatus) {
      case 'completed_full':
        return { color: 'green', fontWeight: 'bold' };
      case 'completed_partial':
        return { color: 'orange', fontWeight: 'bold' };
      case 'stalled':
        return { color: '#FFCCBC', fontWeight: 'bold' }; // Pastel red-orange
      case 'error':
        return { color: '#D14747', fontWeight: 'normal' }; // Muted red, normal weight
      case 'interviewing':
        return { color: 'blue', fontWeight: 'bold' };
      default:
        return { fontWeight: 'bold' };
    }
  };

  const ActivationCard = ({ compact }) => (
    <div className={`li-card ${!compact ? 'li-feed-post' : ''}`} style={compact ? { marginBottom: '16px' } : {}}>
        <div className="li-post-header" style={compact ? { padding: '12px' } : {}}>
            <div className="li-post-actor-image" style={{ backgroundColor: isActivated ? '#057642' : '#666', width: compact ? '32px' : '48px', height: compact ? '32px' : '48px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width={compact ? "16" : "24"} height={compact ? "16" : "24"}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
            </div>
            <div className="li-post-meta">
                <span className="li-post-title" style={compact ? { fontSize: '13px' } : {}}>{t('simulations.pageTitle')}</span>
                {!compact && <span className="li-post-subtitle">{t('simulations.intro')}</span>}
            </div>
        </div>
        
        <div className="li-post-content" style={compact ? { padding: '0 12px 12px' } : {}}>
            <div style={{ padding: compact ? '8px' : '16px', backgroundColor: isActivated ? '#e6f7ee' : '#f3f2ef', borderRadius: '8px', marginTop: '10px' }}>
                {isActivated ? (
                    <div style={{ display: 'flex', alignItems: 'center', color: '#057642', fontSize: compact ? '12px' : '14px' }}>
                        <span style={{ fontWeight: '600' }}>{t('simulations.statusActivated')}</span>
                    </div>
                ) : (
                    <p style={{ fontSize: compact ? '12px' : '14px', margin: 0 }}>{t('simulations.consentText')}</p>
                )}
            </div>
        </div>

        <div className="li-post-footer-actions" style={{ justifyContent: 'center' }}>
            {isActivated ? (
                <button onClick={handleDeactivation} disabled={isLoading} className="li-btn-outline-primary" style={{ borderColor: '#cc1016', color: '#cc1016', width: compact ? '100%' : 'auto' }}>
                    {isLoading ? t('processing') : t('simulations.deactivateButton')}
                </button>
            ) : (
                <button onClick={handleActivation} disabled={isLoading} className="li-btn-primary" style={{ width: compact ? '100%' : 'auto' }}>
                    {isLoading ? t('processing') : t('simulations.activateButton')}
                </button>
            )}
        </div>
    </div>
  );

  return (
    <div className="li-layout">
      <div className="li-container">
        
        {/* Left Sidebar */}
        <aside className="li-sidebar-left">
          <div className="li-card li-profile-card">
            <div className="li-card-background" style={{ backgroundColor: '#a0b4b7' }}></div>
            <div className="li-card-content">
              <img 
                src={userProfile?.avatar_url || defaultAvatar} 
                alt="Profile" 
                className="li-avatar"
              />
              <div className="li-identity">
                <h3 className="li-name">
                  {userProfile?.firstName} {userProfile?.lastName || user?.email}
                </h3>
                <p className="li-headline">
                    {isActivated ? 'AI Agent Active' : 'AI Agent Inactive'}
                </p>
              </div>
            </div>
          </div>

          {/* For HM/Admin: Show small Activation Card here */}
          {isHmOrAdmin && (
            <>
                <ActivationCard compact={true} />
                
                <div className="li-card">
                    <div className="li-card-header" style={{ backgroundColor: '#eef3f8', padding: '12px 16px' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>{t('simulations.theTechnologyTitle')}</h3>
                    </div>
                    
                    <div style={{ padding: '12px' }}>
                        <div style={{ marginBottom: '12px' }}>
                            <h4 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(0,0,0,0.9)', marginBottom: '4px' }}>{t('simulations.candidateFilteringTitle')}</h4>
                            <p style={{ fontSize: '12px', color: 'rgba(0,0,0,0.7)', lineHeight: '1.4' }}>
                                {t('simulations.candidateFilteringDescription', {
                                  interpolation: { escapeValue: false }
                                })}
                            </p>
                        </div>

                        <div>
                            <h4 style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(0,0,0,0.9)', marginBottom: '4px' }}>{t('simulations.aiSimulationsTitle')}</h4>
                            <p style={{ fontSize: '12px', color: 'rgba(0,0,0,0.7)', lineHeight: '1.4' }}>
                                {t('simulations.aiSimulationsDescription', {
                                  interpolation: { escapeValue: false }
                                })}
                            </p>
                        </div>
                    </div>
                </div>
            </>
          )}
        </aside>

        {/* Main Feed */}
        <main className="li-main-feed">
            
            {isHmOrAdmin ? (
                <>
                    {/* Simulation List for HM/Admin */}
                    <div className="li-card">
                        <div className="li-card-header" style={{ backgroundColor: '#A0B4B7', padding: '12px 16px' }}>
                            <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>All Hiring Simulations</h2>
                        </div>
                        <div className="li-card-body" style={{ paddingTop: '16px' }}>
                            {simulations.length === 0 ? (
                                <p style={{ color: '#666' }}>No simulations found. Start a new hiring process to see them here.</p>
                            ) : (
                                simulations.map(sim => (
                                    <div key={sim.id} className="li-feed-post" style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '16px', marginBottom: '16px' }}>
                                        <div className="li-post-header" style={{ paddingLeft: 0, paddingRight: 0 }}>
                                            <div className="li-post-meta">
                                                <Link 
                                                    to={`/hm-tasks/${sim.jd_id}`} 
                                                    className="li-post-title" 
                                                    style={{ 
                                                        textDecoration: 'none', 
                                                        fontSize: '15px', 
                                                        color: '#0a66c2', 
                                                        fontWeight: '600' 
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                                    onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                                                >
                                                    Simulation for JD: {sim.jd_id}
                                                </Link>
                                                <span className="li-post-subtitle" style={{ marginTop: '4px' }}>
                                                    Created: {sim.created_at?.toDate ? sim.created_at.toDate().toLocaleString() : 'Unknown'}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                Status: <span style={getStatusStyle(sim.status)}>{t(`status.${sim.status.toLowerCase()}`, sim.status)}</span>
                                                {(sim.status === 'stalled' || sim.status === 'error') && (
                                                    <div 
                                                        style={{ display: 'flex', alignItems: 'center', cursor: 'help', color: '#666' }}
                                                        title={sim.status === 'stalled' 
                                                            ? "The simulation is waiting for more candidates. The Recruiter agent could not find new matching profiles in the pool."
                                                            : "An unexpected error occurred during the simulation execution."
                                                        }
                                                    >
                                                        <Info size={16} />
                                                    </div>
                                                )}
                                            </span>
                                            <span>
                                                <span style={{ marginRight: '4px' }}>Candidates: {sim.candidates ? Object.keys(sim.candidates).length : 0}</span>
                                                <span 
                                                    style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help', color: '#666', verticalAlign: 'middle' }}
                                                    title="Number of AI twins (AI agents) interviewed by your AI team (Recruiter, Team Members, HM) after being filtered via ANS (Agent Network System - the Nexus of connected AI agents) search."
                                                >
                                                    <Info size={14} />
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {/* Standard Candidate View */}
                    <ActivationCard compact={false} />
                    
                    <div className="li-card li-feed-post">
                        <div className="li-card-header" style={{ backgroundColor: '#eef3f8', padding: '12px 16px' }}>
                            <h3 style={{ margin: 0 }}>{t('simulations.howItWorksTitle')}</h3>
                        </div>
                        <div className="li-card-body">
                            <p style={{ marginBottom: '12px' }}>{t('simulations.howItWorksP1')}</p>
                            <p>{t('simulations.howItWorksP2')}</p>
                        </div>
                    </div>
                </>
            )}

        </main>

        {/* Right Sidebar */}
        <aside className="li-sidebar-right">
            {isHmOrAdmin && (
                <div className="li-card li-feed-post">
                    <div className="li-card-header" style={{ backgroundColor: '#eef3f8', padding: '12px 16px' }}>
                        <h3 style={{ margin: 0 }}>{t('simulations.howItWorksTitle')}</h3>
                    </div>
                    <div className="li-card-body">
                        <p style={{ marginBottom: '12px', fontSize: '13px' }}>{t('simulations.howItWorksP1')}</p>
                    </div>
                </div>
            )}

            <div className="li-card li-news-card">
                <div className="li-card-header" style={{ backgroundColor: '#eef3f8', padding: '12px 16px' }}>
                    <h3 style={{ margin: 0 }}>{t('simulations.quickTipsTitle')}</h3>
                </div>
                <div className="li-card-body">
                    <ul className="li-list-groups">
                        <li style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <Link to="/chat" className="li-news-item" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'flex-start', padding: '12px' }}>
                                <span className="li-news-bullet" style={{ marginTop: '6px' }}></span>
                                <div className="li-news-content">
                                    <span className="li-news-title" style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>{t('simulations.quickTip1Title')}</span>
                                    <span className="li-news-subtitle" style={{ color: '#666', fontSize: '12px' }}>{t('simulations.quickTip1Subtitle')}</span>
                                </div>
                            </Link>
                        </li>
                        <li style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <Link to="/profile" className="li-news-item" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'flex-start', padding: '12px' }}>
                                <span className="li-news-bullet" style={{ marginTop: '6px' }}></span>
                                <div className="li-news-content">
                                    <span className="li-news-title" style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>{t('simulations.quickTip2Title')}</span>
                                    <span className="li-news-subtitle" style={{ color: '#666', fontSize: '12px' }}>{t('simulations.quickTip2Subtitle')}</span>
                                </div>
                            </Link>
                        </li>
                         <li style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <Link to="/candidate-interviews" className="li-news-item" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'flex-start', padding: '12px' }}>
                                <span className="li-news-bullet" style={{ marginTop: '6px' }}></span>
                                <div className="li-news-content">
                                    <span className="li-news-title" style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>{t('simulations.quickTip3Title')}</span>
                                    <span className="li-news-subtitle" style={{ color: '#666', fontSize: '12px' }}>{t('simulations.quickTip3Subtitle')}</span>
                                </div>
                            </Link>
                        </li>
                        {vtcId && (
                             <li style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <Link to={`/digital-twin/${vtcId}`} className="li-news-item" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'flex-start', padding: '12px' }}>
                                    <span className="li-news-bullet" style={{ marginTop: '6px' }}></span>
                                    <div className="li-news-content">
                                        <span className="li-news-title" style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>{t('simulations.quickTip4Title')}</span>
                                        <span className="li-news-subtitle" style={{ color: '#666', fontSize: '12px' }}>{t('simulations.quickTip4Subtitle')}</span>
                                    </div>
                                </Link>
                            </li>
                        )}
                         <li>
                            <Link to="/agent-performance" className="li-news-item" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'flex-start', padding: '12px' }}>
                                <span className="li-news-bullet" style={{ marginTop: '6px' }}></span>
                                <div className="li-news-content">
                                    <span className="li-news-title" style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>{t('simulations.quickTip5Title')}</span>
                                    <span className="li-news-subtitle" style={{ color: '#666', fontSize: '12px' }}>{t('simulations.quickTip5Subtitle')}</span>
                                </div>
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
             <div className="li-footer-links">
                 <Link to="/about">{t('footer.about')}</Link>
                 <Link to="/accessibility">{t('footer.accessibility')}</Link>
                 <Link to="/help-center">{t('footer.helpCenter')}</Link>
                 <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                 <span>{t('auth.footer.rights')}</span>
             </div>
        </aside>
      </div>
    </div>
  );
}

export default ActivateAgent;